import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';

import BlankSlate from '~/components/patterns/messages/embedded/BlankSlate';
import CodeViewer from '~/components/patterns/tables/datatables/CodeViewer';
import DatatableContainer from '~/components/patterns/tables/datatables/DatatableContainer';
import DatatableOverlay from '~/components/patterns/tables/datatables/DatatableOverlay';
import LoadingDots, {
	LoadingDotsSize,
} from '~/components/patterns/loaders/LoadingDots';

import {
	useRenderedDomSnapshotQuery,
} from './PageSnapshotRenderedDom.gql';



const messages = defineMessages({
	notAvailable: {
		id: 'ui.snapshotViewer.overlay.renderedDomNotAvailable',
	},
	notRendered: {
		id: 'ui.snapshotViewer.overlay.javascriptRenderingNotEnabled',
	},
	notTracked: {
		id: 'ui.snapshotViewer.overlay.renderedDomNotTracked',
	},
	tooLarge: {
		id: 'ui.snapshotViewer.overlay.tooLarge',
	},
	truncatedLinePopup: {
		id: 'ui.snapshotViewer.truncatedLinePopup',
	},
});



type Props = {
	height: number,
	timestamp: CK.Timestamp,
	urlId: CK.UrlId,
	websiteId: CK.WebsiteId,
	width: number,
};

const PageSnapshotRenderedDom: React.FC<Props> = (props) => {
	const {
		height,
		timestamp,
		urlId,
		websiteId,
		width,
	} = props;

	const {
		data,
		loading,
	} = useRenderedDomSnapshotQuery({
		variables: {
			timestamp,
			urlId,
			websiteId,
		},
	});

	const lines = data?.responseSnapshot?.dom?.rows ?? [];
	const longestLine = data?.responseSnapshot?.dom?.longestLineCharacters ?? undefined;

	return (
		<DatatableContainer
			overlay={
				loading ? (
					<DatatableOverlay>
						<LoadingDots size={LoadingDotsSize.Large} />
					</DatatableOverlay>
				) : data?.responseSnapshot?.isResponseBodyTracked === false ? (
					<DatatableOverlay>
						<BlankSlate>
							<FormattedMessage {...messages.notTracked} />
						</BlankSlate>
					</DatatableOverlay>
				) : data?.responseSnapshot?.isRendered === false ? (
					<DatatableOverlay>
						<BlankSlate>
							<FormattedMessage {...messages.notRendered} />
						</BlankSlate>
					</DatatableOverlay>
				) : data?.responseSnapshot?.dom === null ? (
					<DatatableOverlay>
						<BlankSlate>
							<FormattedMessage {...messages.notAvailable} />
						</BlankSlate>
					</DatatableOverlay>
				) : data?.responseSnapshot?.dom?.isTooLarge ? (
					<DatatableOverlay>
						<BlankSlate>
							<FormattedMessage {...messages.tooLarge} />
						</BlankSlate>
					</DatatableOverlay>
				) : null
			}
		>
			<CodeViewer
				height={height}
				lines={lines}
				longestLine={longestLine}
				truncateLineAt={1000}
				truncatePopup={(
					<FormattedMessage {...messages.truncatedLinePopup} />
				)}
				width={width}
			/>
		</DatatableContainer>
	);
};



export default PageSnapshotRenderedDom;
